import { ApiFactory } from '../api/agwasuite-api-factory';
import { UserDirectoryApi } from '../api/userdirectory/user-directory-api';
import type {
  GetGroupUserIdsResult,
  UserDirectoryUserDto,
} from '../api/agwa-api-model';
import { userDirectoryInstance } from './user-directory-instance';

export interface GroupUserIdsSnapshot {
  groupId: string;
  userIds: string[]; // stable ordering from server
  version: string; // server computed version token (also ETag)
  fetchedAt: number; // ms
}

export interface GroupUserIdsCacheEntry {
  snapshot?: GroupUserIdsSnapshot;
  ttlMs: number;
  lastAttempt?: number;
}

/** Told the company's people list as it now is, after a re-ask found it changed. */
export type GroupUserIdsChanged = (snapshot: GroupUserIdsSnapshot) => void;

/**
 * The signed-in person's company's people, as ids with a version, read from
 * GET /api/UserDirectory/GroupUserIds.
 *
 * WHO MAY ASK: only a person who may read the company's roster (the server's rule since #398 C5,
 * D-132 rule 1; on the client, access.mayReadRoster). The cache never checks this itself: its one
 * caller, the GDC owner picker, asks only when the person may. Nothing asks at sign-in.
 */
export interface GroupUserIdsCacheApi {
  getGroupUserIds(groupId: string): Promise<GroupUserIdsSnapshot | undefined>;
  prime(snapshot: GroupUserIdsSnapshot): void;
  /**
   * Keep the company's list current while watched: when the user directory's user-updated event
   * delivers one of the company's people (a person of that company, or one the list holds), the
   * list is asked for again with its version, and onChanged is told when the version moved.
   * Returns the function that stops this watcher.
   */
  watch(groupId: string, onChanged: GroupUserIdsChanged): () => void;
  /**
   * Forget every list and stop every watch. A list belongs to the person who was allowed to read
   * it, so it goes when the signed-in person changes or signs out.
   */
  stopAll(): void;
}

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 min

/** One company's watchers, sharing one directory subscription. */
interface GroupWatch {
  watchers: Set<GroupUserIdsChanged>;
  stopDirectory: () => void;
}

class GroupUserIdsCacheImpl implements GroupUserIdsCacheApi {
  private entries = new Map<string, GroupUserIdsCacheEntry>();
  private watches = new Map<string, GroupWatch>();
  /** Companies with a re-ask in flight, and those edited again while it was. */
  private reasking = new Set<string>();
  private reaskAgain = new Set<string>();
  /**
   * Moves on stopAll, so an answer to a request sent for the previous person is dropped rather
   * than kept for the next one.
   */
  private generation = 0;

  private api() {
    return ApiFactory.authenticated(UserDirectoryApi);
  }

  prime(snapshot: GroupUserIdsSnapshot): void {
    const existing = this.entries.get(snapshot.groupId);
    this.entries.set(snapshot.groupId, {
      snapshot,
      ttlMs: existing?.ttlMs ?? DEFAULT_TTL_MS,
      lastAttempt: Date.now(),
    });
  }

  /** Conditional fetch logic. Uses If-None-Match for 304 optimization. */
  async getGroupUserIds(
    groupId: string
  ): Promise<GroupUserIdsSnapshot | undefined> {
    groupId = (groupId || '').trim().toLowerCase();
    if (!groupId) return undefined;
    const entry = this.entries.get(groupId);
    const now = Date.now();
    const stale =
      !entry?.snapshot || now - entry.snapshot.fetchedAt > entry.ttlMs;
    if (!stale && entry?.snapshot) {
      return entry.snapshot; // fresh
    }
    // Avoid thundering herd: if we just attempted within 2s return existing
    if (entry?.lastAttempt && now - entry.lastAttempt < 2000) {
      return entry.snapshot; // unresolved but prevent spam
    }
    return this.ask(groupId);
  }

  /**
   * Asks the server for the list, sending the version held so an unchanged list costs a 304.
   * Freshness and the burst guard are the caller's business: getGroupUserIds applies them, and a
   * re-ask after an edit deliberately does not, because a list fetched a moment ago is exactly
   * the one the edit made stale.
   */
  private async ask(
    groupId: string
  ): Promise<GroupUserIdsSnapshot | undefined> {
    const entry = this.entries.get(groupId);
    const now = Date.now();
    const generation = this.generation;
    this.entries.set(groupId, {
      ...(entry || { ttlMs: DEFAULT_TTL_MS }),
      lastAttempt: now,
      snapshot: entry?.snapshot,
    });
    const api = this.api();
    const resp = await api.getGroupUserIds(entry?.snapshot?.version);
    // Signed out, or someone else signed in, while this was on its way.
    if (generation !== this.generation) return undefined;
    if (resp.status === 304) {
      // Not modified -> update freshness timestamp
      if (entry?.snapshot) {
        entry.snapshot.fetchedAt = now;
        entry.lastAttempt = now;
        return entry.snapshot;
      }
      return undefined; // no existing snapshot? treat as cache miss
    }
    if (!resp.success) {
      // Failure -> keep old snapshot, let caller handle undefined
      return entry?.snapshot;
    }
    const data: GetGroupUserIdsResult = resp.data;
    const snapshot: GroupUserIdsSnapshot = {
      groupId: data.groupId.toLowerCase(),
      userIds: data.userIds.slice(),
      version: data.version,
      fetchedAt: now,
    };
    this.prime(snapshot);
    // Prefetch user details for any new IDs (fire and forget)
    const userCache = userDirectoryInstance();
    userCache.preFetch(snapshot.userIds).catch(() => {});
    return snapshot;
  }

  watch(groupId: string, onChanged: GroupUserIdsChanged): () => void {
    groupId = (groupId || '').trim().toLowerCase();
    if (!groupId) return () => {};
    let watch = this.watches.get(groupId);
    if (!watch) {
      const stopDirectory = userDirectoryInstance().subscribeEdits((user) => {
        if (this.namesOneOfItsPeople(groupId, user)) void this.reask(groupId);
      });
      watch = { watchers: new Set(), stopDirectory };
      this.watches.set(groupId, watch);
    }
    const watchers = watch.watchers;
    watchers.add(onChanged);
    return () => {
      watchers.delete(onChanged);
      const current = this.watches.get(groupId);
      if (current && current.watchers.size === 0) {
        current.stopDirectory();
        this.watches.delete(groupId);
      }
    };
  }

  /**
   * Whether an edited person bears on this company's list: one of its people (joined, or edited),
   * or a person the list holds (who might have left it). Anyone else's edit is not this list's
   * business and costs no request.
   */
  private namesOneOfItsPeople(
    groupId: string,
    user: UserDirectoryUserDto
  ): boolean {
    if ((user.loginGroupId || '').toLowerCase() === groupId) return true;
    const held = this.entries.get(groupId)?.snapshot?.userIds ?? [];
    const id = (user.loginUserId || '').toLowerCase();
    return held.some((h) => h.toLowerCase() === id);
  }

  /**
   * Asks again after an edit, and tells the watchers when the list's version moved. One re-ask per
   * company at a time: an edit that arrives while one is on its way asks once more after it, so
   * the last answer shown was asked after the last edit.
   */
  private async reask(groupId: string): Promise<void> {
    if (this.reasking.has(groupId)) {
      this.reaskAgain.add(groupId);
      return;
    }
    this.reasking.add(groupId);
    try {
      do {
        this.reaskAgain.delete(groupId);
        const before = this.entries.get(groupId)?.snapshot?.version;
        const updated = await this.ask(groupId);
        if (updated && updated.version !== before) {
          this.watches.get(groupId)?.watchers.forEach((cb) => cb(updated));
        }
      } while (this.reaskAgain.has(groupId));
    } finally {
      this.reasking.delete(groupId);
      this.reaskAgain.delete(groupId);
    }
  }

  stopAll(): void {
    for (const watch of this.watches.values()) watch.stopDirectory();
    this.watches.clear();
    this.entries.clear();
    this.reaskAgain.clear();
    this.generation++;
  }
}

let _groupCache: GroupUserIdsCacheApi | undefined;
export function groupUserIdsCacheInstance(): GroupUserIdsCacheApi {
  if (!_groupCache) _groupCache = new GroupUserIdsCacheImpl();
  return _groupCache;
}
